import React, { useState, useRef, useCallback, useEffect, useImperativeHandle, forwardRef } from "react";
import {
  startAutoCapture,
  uploadImage,
  downloadZip,
} from "./roomRendersService";
import { captureCanvas, waitForImages, preloadBackgroundImages } from "./utils/canvasCapture";
import AutoCaptureOverlay from "./AutoCaptureOverlay";

/**
 * AutoCaptureButton component for capturing two specific canvas configurations
 * @param {Object} props
 * @param {number} props.projectId
 * @param {number} props.roomId - Current room ID
 * @param {Object} props.canvasContainerRef - Ref to the canvas container element
 * @param {Function} props.configureCanvasForImageType - Function to configure canvas for specific image type
 * @param {Function} props.forceUpdate - Function to force canvas re-render
 * @param {Function} props.onCaptureComplete - Callback when capture is complete (to restore original state)
 * @param {Function} props.onCaptureSuccess - Callback when capture completes successfully (for reloading images)
 * @param {Array} props.allRooms - Array of all rooms in the project [{id, name}, ...]
 * @param {Function} props.loadRoom - Function to load/switch to a different room
 * @param {Object} props.currentRoom - Current room object
 */
const AutoCaptureButton = forwardRef(({
  projectId,
  roomId,
  canvasContainerRef,
  configureCanvasForImageType,
  forceUpdate,
  onCaptureComplete,
  onCaptureSuccess = null,
  allRooms = [],
  loadRoom = null,
  currentRoom = null,
  onOverlayVisibilityChange = null,
  onCaptureStatusChange = null,
}, ref) => {
  const [status, setStatus] = useState("idle"); // idle, pending, processing, completed, failed
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const [currentRoomName, setCurrentRoomName] = useState("");
  const [totalStepsForOverlay, setTotalStepsForOverlay] = useState(2); // Default to 2 images
  const [completedRooms, setCompletedRooms] = useState(0); // Track completed rooms count
  const [totalRooms, setTotalRooms] = useState(0); // Total rooms to capture
  const [checklistItems, setChecklistItems] = useState([]); // Track all checklist items
  const [completedItems, setCompletedItems] = useState(new Set()); // Track completed item IDs
  const isCapturingRef = useRef(false);
  const originalRoomIdRef = useRef(null);
  const allRoomsRef = useRef(allRooms); // Track latest allRooms value
  const canvasFunctionsRef = useRef({ configureCanvasForImageType, forceUpdate, onCaptureComplete });

  // Expose capture function via ref
  useImperativeHandle(ref, () => ({
    startCapture: (shouldCaptureAll = true) => {
      handleStartCapture(shouldCaptureAll);
    },
  }));

  // Update refs when props change
  useEffect(() => {
    allRoomsRef.current = allRooms;
  }, [allRooms]);

  // Update canvas functions ref when they become available
  useEffect(() => {
    if (configureCanvasForImageType && forceUpdate) {
      canvasFunctionsRef.current = {
        configureCanvasForImageType,
        forceUpdate,
        onCaptureComplete,
      };
    }
  }, [configureCanvasForImageType, forceUpdate, onCaptureComplete]);

  // Default image types for automatic capture (2 types)
  const defaultImageTypes = [
    "external_with_label",
    "internal_with_height_and_width",
  ];

  // Camera views to capture with 1W mode
  const cameraViews = [
    { view: "front", label: "A", suffix: "a" },
    { view: "right", label: "B", suffix: "b" },
    { view: "back", label: "C", suffix: "c" },
    { view: "left", label: "D", suffix: "d" },
  ];

  // Rotation-based logic to determine which views show modules
  // This matches the logic in RoomCanvas.js
  const cameraViewMapper = {
    front: [0],
    right: [90],
    back: [180],
    left: [270],
    top: [0, 90, 180, 270],
  };

  // Check if a camera view has any modules/groups visible
  const hasModulesInView = (cameraViewName, room) => {
    if (!room) return true; // If no room data, capture anyway (fallback)
    
    const allowedRotations = cameraViewMapper[cameraViewName] || [];
    if (allowedRotations.length === 0) return false;

    // Check if any modules have the allowed rotation
    const hasModules = room.modules && room.modules.some((module) =>
      allowedRotations.includes(module.rotation)
    );

    // Check if any groups have the allowed rotation
    const hasGroups = room.groups && room.groups.some((group) =>
      allowedRotations.includes(group.rotation)
    );

    return hasModules || hasGroups;
  };

  /**
   * Wait for render cycles using requestAnimationFrame (no fixed timeout)
   * @param {number} cycles - Number of render cycles to wait (default: 2)
   * @returns {Promise<void>}
   */
  const waitForRenderCycles = (cycles = 2) => {
    return new Promise((resolve) => {
      let count = 0;
      const check = () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            count++;
            if (count >= cycles) {
              resolve();
            } else {
              check();
            }
          });
        });
      };
      check();
    });
  };

  /**
   * Poll to check if measurements are rendered in the canvas
   * @param {HTMLElement} canvasDiv - Canvas container element
   * @param {number} maxWaitMs - Maximum time to wait in ms (default: 3000)
   * @param {number} pollIntervalMs - Polling interval in ms (default: 100)
   * @returns {Promise<void>}
   */
  const waitForMeasurements = (canvasDiv, maxWaitMs = 3000, pollIntervalMs = 100) => {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      const check = () => {
        // Check if measurement elements exist (adjust selector based on your DOM structure)
        const measurements = canvasDiv.querySelectorAll('[class*="measurement"], [class*="dimension"], [data-measurement="true"]');
        const hasMeasurements = measurements.length > 0;

        // Also check if canvas has been updated (compare dimensions or other indicators)
        const canvas = canvasDiv.querySelector('canvas, svg');
        const isReady = hasMeasurements || (canvas && canvas.offsetWidth > 0 && canvas.offsetHeight > 0);

        if (isReady) {
          resolve();
        } else if (Date.now() - startTime > maxWaitMs) {
          // Timeout - proceed anyway (measurements might not be needed or already rendered)
          resolve();
        } else {
          setTimeout(check, pollIntervalMs);
        }
      };
      check();
    });
  };

  /**
   * Poll to check if colors are rendered in the canvas (for external views)
   * Checks for panels with background images or color codes (not default gray)
   * @param {HTMLElement} canvasDiv - Canvas container element
   * @param {number} maxWaitMs - Maximum time to wait in ms (default: 2000)
   * @param {number} pollIntervalMs - Polling interval in ms (default: 100)
   * @returns {Promise<void>}
   */
  const waitForColors = (canvasDiv, maxWaitMs = 3000, pollIntervalMs = 100) => {
    return new Promise((resolve) => {
      const startTime = Date.now();
      let consecutiveChecksWithColors = 0;
      const requiredConsecutiveChecks = 2; // Require 2 consecutive checks to ensure colors are stable

      const check = () => {
        // Find panel nodes (they have the colors applied)
        const panelNodes = canvasDiv.querySelectorAll('[class*="panel-node"], [class*="panel"]');

        if (panelNodes.length === 0) {
          // No panels found yet, keep waiting
          consecutiveChecksWithColors = 0;
          if (Date.now() - startTime > maxWaitMs) {
            resolve(); // Timeout - proceed anyway
          } else {
            setTimeout(check, pollIntervalMs);
          }
          return;
        }

        // Check if panels have colors applied (backgroundImage or non-gray backgroundColor)
        let hasColors = false;
        let panelsWithColors = 0;

        for (const panel of panelNodes) {
          const style = window.getComputedStyle(panel);
          const bgImage = style.backgroundImage;
          const bgColor = style.backgroundColor;

          // Check if panel has background image
          if (bgImage && bgImage !== 'none' && bgImage !== '') {
            hasColors = true;
            panelsWithColors++;
            continue;
          }

          // Check if panel has non-default color
          if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
            // Check if it's not the default gray (#d9d9d9 or rgb(217, 217, 217) or rgba(217, 217, 217, 1))
            const isGray = bgColor.includes('217, 217, 217') ||
                          bgColor === `rgb(${217}, ${217}, ${217})` ||
                          bgColor === 'rgb(217, 217, 217)';

            if (!isGray) {
              hasColors = true;
              panelsWithColors++;
            }
          }
        }

        // Require at least some panels to have colors (not just one)
        // This ensures colors are actually applied, not just a single panel
        const minPanelsWithColors = Math.min(2, Math.max(1, Math.floor(panelNodes.length * 0.3)));
        const hasEnoughColors = panelsWithColors >= minPanelsWithColors;

        if (hasColors && hasEnoughColors) {
          consecutiveChecksWithColors++;
          // Require consecutive checks to ensure colors are stable (not flickering)
          if (consecutiveChecksWithColors >= requiredConsecutiveChecks) {
            resolve();
            return;
          }
        } else {
          consecutiveChecksWithColors = 0;
        }

        if (Date.now() - startTime > maxWaitMs) {
          // Timeout - proceed anyway (colors might not be applicable or already rendered)
          resolve();
        } else {
          setTimeout(check, pollIntervalMs);
        }
      };
      check();
    });
  };

  /**
   * Poll to check if module labels are rendered in the canvas (for external views)
   * Checks for label elements with module labels
   * @param {HTMLElement} canvasDiv - Canvas container element
   * @param {number} maxWaitMs - Maximum time to wait in ms (default: 2000)
   * @param {number} pollIntervalMs - Polling interval in ms (default: 100)
   * @returns {Promise<void>}
   */
  const waitForLabels = (canvasDiv, maxWaitMs = 2000, pollIntervalMs = 100) => {
    return new Promise((resolve) => {
      const startTime = Date.now();

      const check = () => {
        // Find module nodes first (labels are children of modules)
        const moduleNodes = canvasDiv.querySelectorAll('[class*="module-node"], [class*="module"]');

        if (moduleNodes.length === 0) {
          // No modules found yet, keep waiting
          if (Date.now() - startTime > maxWaitMs) {
            resolve(); // Timeout - proceed anyway
          } else {
            setTimeout(check, pollIntervalMs);
          }
          return;
        }

        // Check if at least one module has a label element
        // Labels are rendered as divs with fw-bold class and specific styling
        // They contain the module.label text and have black background with circular border radius
        let hasLabels = false;

        // Look for label elements anywhere in the canvas (they might be siblings, not just children)
        // Labels have fw-bold class, black background, circular shape, and contain text
        const allElements = canvasDiv.querySelectorAll('div.fw-bold');

        for (const element of allElements) {
          const style = window.getComputedStyle(element);
          const bgColor = style.backgroundColor;
          const borderRadius = style.borderRadius;
          const hasText = element.textContent && element.textContent.trim().length > 0;
          const zIndex = style.zIndex;

          // Labels have:
          // - Black background (#000 or rgb(0, 0, 0))
          // - Circular border radius (contains '%' or '50%')
          // - High z-index (9999)
          // - Text content
          const isBlackBg = bgColor === 'rgb(0, 0, 0)' ||
                           bgColor === '#000' ||
                           bgColor.includes('0, 0, 0');
          const isCircular = borderRadius && (borderRadius.includes('%') || borderRadius.includes('50'));
          const isHighZIndex = zIndex && parseInt(zIndex) >= 9999;

          if (hasText && isBlackBg && isCircular && isHighZIndex) {
            hasLabels = true;
            break;
          }
        }

        if (hasLabels) {
          resolve();
        } else if (Date.now() - startTime > maxWaitMs) {
          // Timeout - proceed anyway (labels might not be applicable or already rendered)
          resolve();
        } else {
          setTimeout(check, pollIntervalMs);
        }
      };
      check();
    });
  };

  /**
   * Poll to check if room/canvas is ready after loading
   * Only checks if canvas has content - images will be checked separately when needed
   * @param {HTMLElement} canvasDiv - Canvas container element
   * @param {number} maxWaitMs - Maximum time to wait in ms (default: 3000)
   * @param {number} pollIntervalMs - Polling interval in ms (default: 100)
   * @returns {Promise<void>}
   */
  const waitForRoomReady = (canvasDiv, maxWaitMs = 3000, pollIntervalMs = 100) => {
    return new Promise((resolve) => {
      const startTime = Date.now();
      const check = () => {
        // Check if canvas has content (has dimensions and is visible)
        // Don't wait for all images - they'll be checked when capturing
        const canvas = canvasDiv.querySelector('canvas, svg');
        const hasContent = canvas && canvas.offsetWidth > 0 && canvas.offsetHeight > 0;

        if (hasContent) {
          // Wait one render cycle to ensure canvas is painted
          waitForRenderCycles(1).then(resolve);
        } else if (Date.now() - startTime > maxWaitMs) {
          // Timeout - proceed anyway
          resolve();
        } else {
          setTimeout(check, pollIntervalMs);
        }
      };
      check();
    });
  };

  const handleStartCapture = useCallback(async (shouldCaptureAll = true) => {
    if (isCapturingRef.current) {
      return;
    }

    isCapturingRef.current = true;
    
    // Notify parent that capture has started
    if (onCaptureStatusChange) {
      onCaptureStatusChange(true);
    }

    // Show overlay immediately to provide feedback
    setError(null);
    setStatus("pending");
    setProgress(0);
    setCompletedRooms(0);
    setTotalRooms(0);
    setChecklistItems([]);
    setCompletedItems(new Set());
    setShowOverlay(true);

    let effectiveConfigureCanvas = configureCanvasForImageType || canvasFunctionsRef.current?.configureCanvasForImageType;
    let effectiveForceUpdate = forceUpdate || canvasFunctionsRef.current?.forceUpdate;

    if (!effectiveConfigureCanvas || !effectiveForceUpdate) {
      setError("Canvas not ready. Waiting for canvas to initialize...");
      setStatus("pending");

      // Wait up to 5 seconds for functions to be available
      let retries = 0;
      const maxRetries = 50; // 50 * 100ms = 5 seconds

      while (retries < maxRetries && (!effectiveConfigureCanvas || !effectiveForceUpdate)) {
        await new Promise(resolve => setTimeout(resolve, 100));
        // Re-check from ref in case props updated
        effectiveConfigureCanvas = configureCanvasForImageType || canvasFunctionsRef.current?.configureCanvasForImageType;
        effectiveForceUpdate = forceUpdate || canvasFunctionsRef.current?.forceUpdate;
        retries++;
      }

      // Final check
      if (!effectiveConfigureCanvas || !effectiveForceUpdate) {
        setError("Canvas functions not available. Please refresh the page or try again in a moment.");
        setStatus("failed");
        // Keep overlay visible so user can see the error
        // Don't reset isCapturingRef here - let user close manually or retry
        return;
      }

      setError(null);
    }

    // Use the effective functions for the rest of the capture process
    const finalConfigureCanvas = effectiveConfigureCanvas;
    const finalForceUpdate = effectiveForceUpdate;

    // Use the latest allRooms value from ref (in case prop hasn't updated yet)
    // Also check the prop directly as fallback
    const latestAllRooms = allRoomsRef.current || allRooms || [];
    const effectiveAllRooms = latestAllRooms.length > 0 ? latestAllRooms : (allRooms || []);

    try {

      // Get the actual current room ID (use currentRoom prop if available, otherwise use roomId prop)
      const actualCurrentRoomId = currentRoom?.id || roomId;

      // Store the original room ID
      originalRoomIdRef.current = actualCurrentRoomId;

      // Always capture all rooms with automatic toggle configuration
      // For each room: call startAutoCapture API, then capture 2 images (external_with_label and internal_with_height_and_width)
      let roomsToCapture;
      if (effectiveAllRooms.length > 0) {
        // ALWAYS capture all rooms starting from current room, cycling through all
        // This is the expected behavior: click button → capture all rooms automatically
        roomsToCapture = getAllRoomsInOrder(actualCurrentRoomId, effectiveAllRooms);
      } else {
        // Fallback: only current room if no rooms list available
        roomsToCapture = [{ id: actualCurrentRoomId, name: currentRoom?.name || `Room ${actualCurrentRoomId}` }];
      }

      // Calculate total steps: all views (A, B, C, D, T) per room with 1W mode
      // For each base type, capture all 5 views
      // Note: This is the maximum, actual count will be adjusted as we filter out views with no modules
      const maxImagesPerRoom = defaultImageTypes.length * cameraViews.length; // 2 types × 5 views = 10 images per room
      const totalSteps = roomsToCapture.length * maxImagesPerRoom;

      // Create checklist items: all rooms × all image types × all views
      // Items will be filtered out dynamically as we process each room and discover which views have no modules
      const imageTypeDisplayNames = {
        "external_with_label": "External",
        "internal_with_height_and_width": "Internal",
        "current_state": "Current State"
      };

      const items = [];
      roomsToCapture.forEach((room) => {
        defaultImageTypes.forEach((baseType) => {
          cameraViews.forEach((cameraView) => {
            const imageType = `${baseType}_${cameraView.suffix}_1w`;
            const itemId = `${room.id}-${imageType}`;
            items.push({
              id: itemId,
              roomName: room.name || `Room ${room.id}`,
              imageType: imageType,
              imageTypeDisplay: `${imageTypeDisplayNames[baseType] || baseType} ${cameraView.label} (1W)`,
              cameraView: cameraView.view,
            });
          });
        });
      });

      setChecklistItems(items);
      setCompletedItems(new Set());

      // Set total steps for overlay to be per-room (max images per room, will be adjusted as we filter)
      setTotalStepsForOverlay(maxImagesPerRoom);
      // Initialize room tracking
      setTotalRooms(roomsToCapture.length);
      setCompletedRooms(0);

      // Process each room sequentially
      let currentRoomId = actualCurrentRoomId; // Track current room ID as we switch

      for (let roomIndex = 0; roomIndex < roomsToCapture.length; roomIndex++) {
        const targetRoom = roomsToCapture[roomIndex];

        try {
          // Compare room IDs (handle string/number mismatch)
          const roomIdsMatch = targetRoom.id == currentRoomId ||
                              targetRoom.id === currentRoomId ||
                              String(targetRoom.id) === String(currentRoomId);

          // Switch to the target room if it's different from current
          if (!roomIdsMatch && loadRoom) {
            // Reset progress for new room
            setProgress(0);
            // Reset total steps to maximum for this room (will be decremented as we discover skipped views)
            setTotalStepsForOverlay(maxImagesPerRoom);
            setCurrentRoomName(targetRoom.name || `Room ${targetRoom.id}`);
            setStatus("processing");

            // Load the room and wait for it to complete
            try {
              await loadRoom(targetRoom.id, null, null, false, null, true);

              // Get canvas container and poll for room to be ready
              const container = canvasContainerRef?.current;
              if (container) {
                const canvasDiv = container.querySelector("#canvas-div") || container;
                await waitForRoomReady(canvasDiv, 5000, 100);
              } else {
                // Fallback: wait for render cycles if container not available
                await waitForRenderCycles(2);
              }

              currentRoomId = targetRoom.id; // Update tracked room ID
            } catch (loadError) {
              throw new Error(`Failed to load room ${targetRoom.id}: ${loadError.message || loadError}`);
            }
          } else {
            // Reset progress when starting a new room (even if same room)
            setProgress(0);
            // Reset total steps to maximum for this room (will be decremented as we discover skipped views)
            setTotalStepsForOverlay(maxImagesPerRoom);
            setCurrentRoomName(targetRoom.name || `Room ${targetRoom.id}`);
          }

          // Use automatic toggle configuration for all rooms
          // This will configure toggles automatically for each image type (external_with_label and internal_with_height_and_width)
          const useManualSettings = false; // Use automatic toggle configuration

          // Start auto capture API call for this room (room_render call)
          // Note: forceNew parameter is kept for API compatibility but DELETE call is removed
          // The backend POST endpoint will handle creating/updating room renders appropriately
          const response = await startAutoCapture(projectId, targetRoom.id, false);
          setStatus(response.status || "processing");

          // Get the current room data after loading (needed to check which views have modules)
          // Wait a bit for currentRoom to update after loadRoom completes
          // Use a ref to track the latest currentRoom
          let roomDataForFiltering = null;
          if (currentRoom?.id === targetRoom.id) {
            roomDataForFiltering = currentRoom;
          } else {
            // Wait a bit for currentRoom to update, then check again
            await waitForRenderCycles(2);
            // Check currentRoomRef if available, or use currentRoom prop
            roomDataForFiltering = currentRoom?.id === targetRoom.id ? currentRoom : null;
          }

          // Filter out checklist items for views that don't have modules (before starting capture)
          if (roomDataForFiltering) {
            const itemsToRemove = [];
            defaultImageTypes.forEach((baseType) => {
              cameraViews.forEach((cameraView) => {
                // Check if this view has modules
                if (!hasModulesInView(cameraView.view, roomDataForFiltering)) {
                  const imageType = `${baseType}_${cameraView.suffix}_1w`;
                  const itemId = `${targetRoom.id}-${imageType}`;
                  itemsToRemove.push(itemId);
                }
              });
            });
            
            // Remove items that won't be captured from the checklist
            if (itemsToRemove.length > 0) {
              setChecklistItems(prev => prev.filter(item => !itemsToRemove.includes(item.id)));
              // Update total steps to reflect removed items
              setTotalStepsForOverlay(prev => Math.max(0, prev - itemsToRemove.length));
            }
          }

          // Capture and upload all views (A, B, C, D, T) for each image type
          // This will automatically configure toggles for each view
          // Pass the effective functions to ensure they're available
          // Pass flag indicating if we just switched rooms (for optimization)
          const justSwitchedRooms = !roomIdsMatch && loadRoom;
          await captureAndUploadAllImagesForRoom(
            targetRoom.id,
            roomIndex,
            roomsToCapture.length,
            totalSteps,
            useManualSettings, // false = use automatic toggle configuration
            finalConfigureCanvas, // Pass the effective configure function
            finalForceUpdate, // Pass the effective forceUpdate function
            justSwitchedRooms, // Indicate if we just switched rooms
            defaultImageTypes, // Pass base image types
            cameraViews, // Pass camera views
            roomDataForFiltering // Pass room data to filter views with no modules
          );

          // Mark this room as completed
          setCompletedRooms(roomIndex + 1);
        } catch (roomError) {
          // Continue to next room even if this one fails
          setError(
            `Failed to capture room ${targetRoom.name || targetRoom.id}: ${
              roomError.error || roomError.message || "Unknown error"
            }. Continuing with next room...`
          );
          // Don't throw - continue to next room
        }
      }

      // Return to original room (only if we switched rooms)
      if (shouldCaptureAll && originalRoomIdRef.current && loadRoom) {
        const finalRoomId = currentRoom?.id || currentRoomId;
        if (originalRoomIdRef.current !== finalRoomId) {
          setCurrentRoomName("Returning to original room...");
          await loadRoom(originalRoomIdRef.current, null, null, false, null, true);
          // Poll for room to be ready instead of fixed wait
          const container = canvasContainerRef?.current;
          if (container) {
            const canvasDiv = container.querySelector("#canvas-div") || container;
            await waitForRoomReady(canvasDiv, 5000, 100);
          } else {
            await waitForRenderCycles(2);
          }
        }
      }

      // Set final status and progress
      setProgress(totalSteps);
      setStatus("completed");

      // Wait for render cycle to ensure React state updates are applied before overlay renders
      await waitForRenderCycles(1);

      // Call success callback to reload images
      if (onCaptureSuccess) {
        onCaptureSuccess();
      }

      // Auto-close overlay after a brief delay (1 second to show completion message)
      setTimeout(() => {
        setShowOverlay(false);
        setStatus("idle");
        setProgress(0);
        setError(null);
        // Restore original state if needed
        if (onCaptureComplete) {
          onCaptureComplete();
        }
      }, 1000);
    } catch (error) {
      setError(error.error || error.message || "Failed to start auto capture");
      setStatus("failed");

      // Try to return to original room even on error
      if (originalRoomIdRef.current && loadRoom) {
        loadRoom(originalRoomIdRef.current, null, null, false, null, true);
      }
    } finally {
      isCapturingRef.current = false;
      // Notify parent that capture has ended
      if (onCaptureStatusChange) {
        onCaptureStatusChange(false);
      }
    }
  }, [projectId, roomId, configureCanvasForImageType, forceUpdate, allRooms, loadRoom, currentRoom, onCaptureSuccess, onCaptureStatusChange]);

  /**
   * Get all rooms in order starting from the next room after currentRoomId
   * @param {number} currentRoomId - The current room ID
   * @param {Array} allRooms - Array of all rooms
   * @returns {Array} Ordered array of rooms to capture
   */
  const getAllRoomsInOrder = (currentRoomId, allRooms) => {
    if (!allRooms || allRooms.length === 0) {
      return [{ id: currentRoomId }];
    }

    // Find current room index (handle both string and number IDs)
    const currentIndex = allRooms.findIndex(room =>
      room.id == currentRoomId || room.id === currentRoomId || String(room.id) === String(currentRoomId)
    );

    if (currentIndex === -1) {
      // Current room not found, just capture current room
      return [{ id: currentRoomId }];
    }

    // Get rooms starting from next room, then wrap around
    const roomsAfterCurrent = allRooms.slice(currentIndex + 1);
    const roomsBeforeCurrent = allRooms.slice(0, currentIndex);
    const orderedRooms = [...roomsAfterCurrent, ...roomsBeforeCurrent, allRooms[currentIndex]];

    return orderedRooms;
  };

  /**
   * Capture and upload all images for a specific room
   * @param {number} targetRoomId - The room ID to capture
   * @param {number} roomIndex - Current room index in the sequence
   * @param {number} totalRooms - Total number of rooms to capture
   * @param {number} totalSteps - Total steps (rooms * imageTypes * views)
   * @param {boolean} useManualSettings - If true, don't configure canvas (use manual toggle settings)
   * @param {Function} effectiveConfigureCanvas - Canvas configuration function
   * @param {Function} effectiveForceUpdateFn - Force update function
   * @param {boolean} justSwitchedRooms - If true, we just switched rooms (optimize first image)
   * @param {Array} baseImageTypes - Base image types to capture
   * @param {Array} cameraViewsToUse - Camera views to capture
   * @param {Object} roomData - Room data object to check which views have modules
   */
  const captureAndUploadAllImagesForRoom = async (
    targetRoomId,
    roomIndex,
    totalRooms,
    totalSteps,
    useManualSettings = false,
    effectiveConfigureCanvas = null,
    effectiveForceUpdateFn = null,
    justSwitchedRooms = false,
    baseImageTypes = null,
    cameraViewsToUse = null,
    roomData = null
  ) => {
    // Use passed functions or fall back to props/ref
    const configureFn = effectiveConfigureCanvas || configureCanvasForImageType || canvasFunctionsRef.current?.configureCanvasForImageType;
    const forceUpdateFn = effectiveForceUpdateFn || forceUpdate || canvasFunctionsRef.current?.forceUpdate;
    const setCameraViewFn = canvasFunctionsRef.current?.setCameraView;
    const setWallViewFn = canvasFunctionsRef.current?.setWallView;
    const container = canvasContainerRef?.current;
    if (!container) {
      throw new Error("Canvas container not found");
    }

    // Find the actual canvas div (id="canvas-div")
    const canvasDiv = container.querySelector("#canvas-div") || container;

    let lastError = null;

    // Get image types based on capture mode
    let imageTypesToCapture;
    if (useManualSettings) {
      imageTypesToCapture = ["current_state"]; // Single capture with manual settings
    } else if (baseImageTypes && cameraViewsToUse) {
      // Generate all combinations: baseType_view_1w
      // e.g., ["external_with_label_a_1w", "external_with_label_b_1w", ...]
      // Filter out views that don't have modules using rotation-based logic
      imageTypesToCapture = [];
      baseImageTypes.forEach((baseType) => {
        cameraViewsToUse.forEach((cv) => {
          // Skip views that don't have modules (using same rotation logic as RoomCanvas)
          if (hasModulesInView(cv.view, roomData)) {
            imageTypesToCapture.push(`${baseType}_${cv.suffix}_1w`);
          }
        });
      });
    } else {
      // Fallback: generate all combinations from defaultImageTypes and cameraViews
      // Filter out views that don't have modules using rotation-based logic
      imageTypesToCapture = [];
      defaultImageTypes.forEach((baseType) => {
        cameraViews.forEach((cv) => {
          // Skip views that don't have modules (using same rotation logic as RoomCanvas)
          if (hasModulesInView(cv.view, roomData)) {
            imageTypesToCapture.push(`${baseType}_${cv.suffix}_1w`);
          }
        });
      });
    }

    try {
      for (let i = 0; i < imageTypesToCapture.length; i++) {
        const imageType = imageTypesToCapture[i];
        // Progress is per-room: 0, 1, 2, ..., 9 (for 10 images: 2 types × 5 views)
        const currentStep = i + 1;

        // Note: Views without modules are already filtered out when building imageTypesToCapture
        // and removed from checklist before this loop starts, so we don't need to check again here

        try {
          setStatus("processing");
          setProgress(i); // 0 for first image, 1 for second image

          // Mark this checklist item as in progress (optional - we can show current item differently)
          // The item will be marked as completed after successful upload

          // Configure canvas for this image type (only if not using manual settings)
          // Use the passed functions or fall back to props/ref
          if (!useManualSettings && configureFn) {
            // Extract camera view and wall view from image type if it's a view-specific type
            // Format: "external_with_label_a_1w" -> base: "external_with_label", view: "front", suffix: "a"
            let viewConfig = null;
            if (imageType.includes("_1w")) {
              const viewMatch = imageType.match(/_([a-z])_1w$/);
              if (viewMatch && viewMatch[1]) {
                // Map suffix to view: a->front, b->right, c->back, d->left, t->top
                const suffixToView = { a: "front", b: "right", c: "back", d: "left", t: "top" };
                const suffix = viewMatch[1];
                viewConfig = {
                  cameraView: suffixToView[suffix],
                  wallView: "1", // Always use 1W mode
                };
              }
            }
            
            // Apply the configuration with view config (this will set camera view and wall view)
            configureFn(imageType, viewConfig);
            
            // Also set camera view and wall view directly if functions are available (redundant but ensures it's set)
            if (viewConfig) {
              if (setCameraViewFn) {
                setCameraViewFn(viewConfig.cameraView);
              }
              if (setWallViewFn) {
                setWallViewFn(viewConfig.wallView);
              }
            }

            // Force re-render to apply configuration
            if (forceUpdateFn) {
              forceUpdateFn();
            }
            
            // Wait for camera view change to take effect (especially important for different views)
            if (viewConfig && viewConfig.cameraView) {
              await waitForRenderCycles(2);
              
              // If we don't have room data, check DOM for modules as fallback
              if (!roomData && viewConfig.cameraView) {
                // Check if any module elements are visible in the DOM
                const moduleElements = canvasDiv.querySelectorAll('[data-object-type="module"]');
                const groupElements = canvasDiv.querySelectorAll('[data-object-type="group"]');
                const hasVisibleModules = moduleElements.length > 0 || groupElements.length > 0;
                
                if (!hasVisibleModules) {
                  // No modules visible in this view, remove from checklist and skip capture
                  const itemId = `${targetRoomId}-${imageType}`;
                  setChecklistItems(prev => prev.filter(item => item.id !== itemId));
                  // Also update total steps to reflect the removed item
                  setTotalStepsForOverlay(prev => Math.max(0, prev - 1));
                  continue; // Skip this capture
                }
              }
            }

            // For first image after room switch, we already waited for room ready
            // So we can use fewer render cycles (1 instead of 2)
            const isFirstImageAfterSwitch = justSwitchedRooms && i === 0;
            const renderCycles = isFirstImageAfterSwitch ? 1 : 2;
            await waitForRenderCycles(renderCycles);

            // For measurements, wait for them to be generated and rendered
            // Check base type (handles both "internal_with_height_and_width" and "internal_with_height_and_width_a_1w")
            const isMeasurementType = imageType === "internal_with_height_and_width" || imageType.startsWith("internal_with_height_and_width_");
            if (isMeasurementType) {
              // Force another update to trigger measurement generation
              if (forceUpdateFn) {
                forceUpdateFn();
              }
              // Poll for measurements to be ready (max 3 seconds)
              await waitForMeasurements(canvasDiv, 3000, 100);
              // One more render cycle after measurements are detected
              await waitForRenderCycles(1);
            }

            // For external views, poll to ensure colors and labels are rendered
            // Check base type (handles both "external_with_label" and "external_with_label_a_1w")
            const isExternalType = imageType === "external_with_label" || imageType.startsWith("external_with_label_");
            if (isExternalType) {
              // Force another update to trigger color and label rendering
              if (forceUpdateFn) {
                forceUpdateFn();
              }
              // Wait for render cycles to allow React to apply state changes (especially showColors)
              // flushSync was used in configureCanvasForImageType, but we still need to wait for rendering
              await waitForRenderCycles(3);

              // Poll for colors to be rendered (max 3 seconds, more thorough checking)
              // This ensures colors are actually applied and stable before capture
              await waitForColors(canvasDiv, 3000, 100);

              // Poll for labels to be rendered (max 2 seconds)
              await waitForLabels(canvasDiv, 2000, 100);

              // One more render cycle after colors and labels are detected
              await waitForRenderCycles(2);
            }
          } else if (useManualSettings) {
            // Just wait for one render cycle
            await waitForRenderCycles(1);
          }

          // Wait for <img> tags to load (this already has its own timeout)
          await waitForImages(canvasDiv);

          // CRITICAL: Preload all CSS background images (laminate images from S3)
          // This ensures they're loaded with CORS headers before html2canvas tries to capture them
          // Without this, html2canvas may fail to load cross-origin background images in production
          await preloadBackgroundImages(canvasDiv);

          // Verify canvas div is still available
          const currentCanvasDiv = container.querySelector("#canvas-div") || container;
          if (!currentCanvasDiv) {
            throw new Error("Canvas element not found");
          }

          // Find the inner content div (contains the actual room content with modules)
          const innerContentDiv = Array.from(currentCanvasDiv.children).find(child => {
            const style = window.getComputedStyle(child);
            return style.position === 'absolute';
          }) || currentCanvasDiv;
          
          // Determine which element to capture - prefer innerContentDiv if it has valid dimensions
          let elementToCapture = innerContentDiv;
          const innerRect = innerContentDiv.getBoundingClientRect();
          const canvasRect = currentCanvasDiv.getBoundingClientRect();
          
          // If innerContentDiv has zero dimensions, use canvasDiv instead
          if (innerRect.width === 0 || innerRect.height === 0) {
            console.warn(`[Room ${targetRoomId}] Inner content div has zero dimensions, using canvas div instead`);
            elementToCapture = currentCanvasDiv;
          }
          
          // Validate element dimensions before capture
          const elementRect = elementToCapture.getBoundingClientRect();
          if (elementRect.width === 0 || elementRect.height === 0) {
            throw new Error(`Element to capture has invalid dimensions: ${elementRect.width}x${elementRect.height}`);
          }
          
          console.log(`[Room ${targetRoomId}] Capturing element:`, {
            id: elementToCapture.id,
            className: elementToCapture.className,
            dimensions: `${elementRect.width}x${elementRect.height}`,
            isInnerContent: elementToCapture === innerContentDiv
          });
          
          // CRITICAL: Temporarily hide right pane and adjust overflow to capture full content
          // Find and hide the right pane that covers the canvas
          const rightPane = document.querySelector('.right-pane');
          const originalRightPaneDisplay = rightPane ? rightPane.style.display : null;
          const originalRightPaneVisibility = rightPane ? rightPane.style.visibility : null;
          const originalRightPaneZIndex = rightPane ? rightPane.style.zIndex : null;
          
          // Hide right pane temporarily by moving it completely off-screen
          // This ensures html2canvas can see all content that was behind it
          let originalRightPanePosition = null;
          let originalRightPaneLeft = null;
          let originalRightPaneRight = null;
          
          if (rightPane) {
            // Store original position values
            originalRightPanePosition = rightPane.style.position;
            originalRightPaneLeft = rightPane.style.left;
            originalRightPaneRight = rightPane.style.right;
            
            // Move it completely off-screen to the right (beyond viewport)
            rightPane.style.position = 'fixed';
            rightPane.style.left = '99999px'; // Move far off-screen
            rightPane.style.right = 'auto';
            rightPane.style.visibility = 'hidden';
            rightPane.style.zIndex = '-1';
          }
          
          // Remove overflow-hidden class from canvas div (it's in className, not just style)
          const originalCanvasClass = currentCanvasDiv.className;
          const hasOverflowHidden = currentCanvasDiv.className.includes('overflow-hidden');
          if (hasOverflowHidden) {
            currentCanvasDiv.className = currentCanvasDiv.className.replace(/overflow-hidden/g, 'overflow-visible');
          }
          
          // Remove overflow restrictions on canvas div and parent containers to capture full content
          const originalOverflow = currentCanvasDiv.style.overflow;
          const originalOverflowX = currentCanvasDiv.style.overflowX;
          const originalOverflowY = currentCanvasDiv.style.overflowY;
          
          // Also check parent containers
          let parent = currentCanvasDiv.parentElement;
          const parentOverflows = [];
          while (parent && parent !== document.body) {
            const computedStyle = window.getComputedStyle(parent);
            if (computedStyle.overflow === 'hidden' || computedStyle.overflowX === 'hidden' || computedStyle.overflowY === 'hidden') {
              parentOverflows.push({
                element: parent,
                overflow: parent.style.overflow,
                overflowX: parent.style.overflowX,
                overflowY: parent.style.overflowY
              });
              parent.style.overflow = 'visible';
              parent.style.overflowX = 'visible';
              parent.style.overflowY = 'visible';
            }
            parent = parent.parentElement;
          }
          
          // Set canvas div overflow to visible (both style and ensure class doesn't override)
          currentCanvasDiv.style.overflow = 'visible';
          currentCanvasDiv.style.overflowX = 'visible';
          currentCanvasDiv.style.overflowY = 'visible';
          currentCanvasDiv.style.overflow = 'visible !important'; // Force override
          
          // Wait for DOM to update after style changes
          await waitForRenderCycles(2);
          
          try {
            // One final render cycle to ensure everything is painted
            const isFirstImageAfterSwitch = justSwitchedRooms && i === 0;
            if (!isFirstImageAfterSwitch) {
              await waitForRenderCycles(1);
            }

            // Calculate full content dimensions including overflow
            // The innerContentDiv contains all modules, so we need its full bounds
            const fullWidth = Math.max(
              elementToCapture.scrollWidth || elementRect.width,
              innerContentDiv.scrollWidth || innerRect.width,
              elementRect.width
            );
            const fullHeight = Math.max(
              elementToCapture.scrollHeight || elementRect.height,
              innerContentDiv.scrollHeight || innerRect.height,
              elementRect.height
            );
            
            console.log(`[Room ${targetRoomId}] Capture dimensions:`, {
              elementRect: `${elementRect.width}x${elementRect.height}`,
              innerRect: `${innerRect.width}x${innerRect.height}`,
              scrollSize: `${elementToCapture.scrollWidth}x${elementToCapture.scrollHeight}`,
              fullSize: `${fullWidth}x${fullHeight}`
            });

            // Capture the element with full dimensions to include overflow content
            // Pass scrollWidth/scrollHeight to ensure html2canvas captures full element
            const captureOptions = {
              width: Math.max(elementToCapture.scrollWidth, innerContentDiv.scrollWidth, elementRect.width),
              height: Math.max(elementToCapture.scrollHeight, innerContentDiv.scrollHeight, elementRect.height),
            };
            
            console.log(`[Room ${targetRoomId}] Capturing with options:`, captureOptions);
            
            // Capture the element - html2canvas will capture full element content
            // We're capturing innerContentDiv which contains all modules including those behind right pane
            const blob = await captureCanvas(elementToCapture, captureOptions);

            // Restore overflow on canvas div
            currentCanvasDiv.style.overflow = originalOverflow || '';
            currentCanvasDiv.style.overflowX = originalOverflowX || '';
            currentCanvasDiv.style.overflowY = originalOverflowY || '';
            
            // Restore original className (including overflow-hidden)
            if (hasOverflowHidden) {
              currentCanvasDiv.className = originalCanvasClass;
            }
            
            // Restore overflow on parent containers
            parentOverflows.forEach(({ element, overflow, overflowX, overflowY }) => {
              element.style.overflow = overflow || '';
              element.style.overflowX = overflowX || '';
              element.style.overflowY = overflowY || '';
            });
            
            // Restore right pane position, visibility, and z-index
            if (rightPane) {
              // Restore position
              if (originalRightPanePosition !== null && originalRightPanePosition !== '') {
                rightPane.style.position = originalRightPanePosition;
              } else {
                rightPane.style.position = '';
              }
              if (originalRightPaneLeft !== null && originalRightPaneLeft !== '') {
                rightPane.style.left = originalRightPaneLeft;
              } else {
                rightPane.style.left = '';
              }
              if (originalRightPaneRight !== null && originalRightPaneRight !== '') {
                rightPane.style.right = originalRightPaneRight;
              } else {
                rightPane.style.right = '';
              }
              
              // Restore visibility
              if (originalRightPaneVisibility !== null) {
                rightPane.style.visibility = originalRightPaneVisibility;
              } else {
                rightPane.style.visibility = '';
              }
              
              // Restore z-index
              if (originalRightPaneZIndex !== null) {
                rightPane.style.zIndex = originalRightPaneZIndex;
              } else {
                rightPane.style.zIndex = '';
              }
              
              // Restore display
              if (originalRightPaneDisplay !== null) {
                rightPane.style.display = originalRightPaneDisplay;
              }
            }

            // Verify blob was created
            if (!blob || blob.size === 0) {
              throw new Error("Failed to capture canvas - empty blob");
            }

            // Upload to backend
            const uploadImageType = imageType === "current_state"
              ? "external_with_label"
              : imageType;

            const result = await uploadImage(
              projectId,
              targetRoomId,
              uploadImageType,
              blob
            );
            
            // Mark this checklist item as completed
            const itemId = `${targetRoomId}-${imageType}`;
            setCompletedItems(prev => {
              const newSet = new Set(prev);
              newSet.add(itemId);
              return newSet;
            });

            setProgress(currentStep);
            if (useManualSettings && result.status === "completed") {
              setStatus("completed");
            } else if (result.status && result.status !== "completed") {
              setStatus(result.status);
            }
          } catch (error) {
            // Restore overflow on error
            currentCanvasDiv.style.overflow = originalOverflow;
            throw error;
          }
        } catch (error) {
          lastError = error;
          setError(
            `Failed to capture ${imageType} for room ${targetRoomId}: ${
              error.error || error.message || "Unknown error"
            }`
          );
          setStatus("failed");
          // Restore state even on error (only if we configured it)
          if (!useManualSettings && onCaptureComplete) {
            onCaptureComplete();
          }
          throw error;
        }
      }
    } finally {
      // Final restore to ensure state is correct (only if we configured it)
      if (!useManualSettings && onCaptureComplete) {
        onCaptureComplete();
      }
    }

    if (lastError) {
      throw lastError;
    }
  };

  const handleDownload = async () => {
    try {
      setError(null);
      await downloadZip(projectId, roomId);
      setShowOverlay(false);
      setStatus("idle");
      setProgress(0);
    } catch (error) {
      setError(error.error || error.message || "Failed to download ZIP file");
    }
  };

  const handleClose = () => {
    setShowOverlay(false);
    setStatus("idle");
    setProgress(0);
    setError(null);
    isCapturingRef.current = false;
    // Notify parent that capture has ended
    if (onCaptureStatusChange) {
      onCaptureStatusChange(false);
    }
    // Restore original state if needed
    if (onCaptureComplete) {
      onCaptureComplete();
    }
  };

  // Ensure overlay stays visible when there's an error or failed status
  // This prevents the overlay from closing immediately after showing an error
  useEffect(() => {
    if ((status === "failed" && error) || status === "processing" || status === "pending") {
      if (!showOverlay) {
        setShowOverlay(true);
      }
    }
  }, [status, error, showOverlay]);

  // Notify parent component when overlay visibility changes
  // This allows parent to keep component mounted even if other conditions change
  useEffect(() => {
    if (onOverlayVisibilityChange) {
      onOverlayVisibilityChange(showOverlay);
    }
  }, [showOverlay, onOverlayVisibilityChange]);

  const isDisabled =
    status === "processing" || status === "pending" || isCapturingRef.current;

  return (
    <>
      <button
        id="auto-capture-button"
        className="btn pointer d-flex align-items-center justify-content-center"
        onClick={() => handleStartCapture(true).catch(() => {})}
        disabled={isDisabled}
        style={{
          border: "none",
          background: "transparent",
          padding: 0,
          cursor: isDisabled ? "not-allowed" : "pointer",
          pointerEvents: isDisabled ? "none" : "auto"
        }}
      >
        <i className={`fa-solid fa-camera fs-5 ${isDisabled ? "" : "text-white"}`} />
      </button>

      <AutoCaptureOverlay
        isVisible={showOverlay}
        progress={progress}
        total={totalStepsForOverlay}
        status={status}
        error={error}
        onClose={handleClose}
        onDownload={handleDownload}
        currentRoomName={currentRoomName}
        completedRooms={completedRooms}
        totalRooms={totalRooms}
        checklistItems={checklistItems}
        completedItems={Array.from(completedItems)}
      />
    </>
  );
});

AutoCaptureButton.displayName = "AutoCaptureButton";

export default AutoCaptureButton;

